import { useEffect, useState } from 'react';
import type { UnitSystem } from '@lift2fit/shared-types';

/**
 * Unit preference is a single user setting shared by every surface (The Grid,
 * the Programme, the coach report). Canonical storage stays in kilograms — this
 * only controls display, exactly as the UI/UX spec requires.
 */
const KEY = 'l2f.unit';

type Listener = (unit: UnitSystem) => void;
const listeners = new Set<Listener>();

export function readUnitPreference(): UnitSystem {
  try {
    return localStorage.getItem(KEY) === 'imperial' ? 'imperial' : 'metric';
  } catch {
    return 'metric';
  }
}

export function writeUnitPreference(unit: UnitSystem): void {
  try {
    localStorage.setItem(KEY, unit);
  } catch {
    /* storage blocked — the session still converts correctly */
  }
  for (const listener of listeners) listener(unit);
}

export function useUnitPreference(): {
  unit: UnitSystem;
  setUnit: (unit: UnitSystem) => void;
  toggle: () => void;
} {
  const [unit, setUnit] = useState<UnitSystem>(readUnitPreference);

  useEffect(() => {
    const listener: Listener = (next) => setUnit(next);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  return {
    unit,
    setUnit: writeUnitPreference,
    toggle: () => writeUnitPreference(unit === 'metric' ? 'imperial' : 'metric'),
  };
}
